import { useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectResources, resourceLookup } from './ducks/index.js';

/**
 * @typedef {Object} UseResourcesHookResult
 * @property {boolean} isLoading - Whether any of the keys are still loading their urls
 * @property {Object<string, string>} resources - Key Value Object where the key is the resource key and the value is the url
 */

/**
 * React hook fetches and returns urls of passed resourcekeys
 *
 * @param {Array<String>} [keys=[]] - Array of keys to fetch urls for
 * @returns {UseResourcesHookResult}
 */
const useResources = keys => {
	const dispatch = useDispatch();
	const allResources = useSelector(selectResources);

	// Find out if we are missing any keys
	const missingKeys = useMemo(
		() => keys.filter(key => typeof allResources[key] === 'undefined'),
		[keys, allResources]
	);
	const isMissingKeys = missingKeys.length > 0;

	// Fetch any keys we are missing
	useEffect(() => {
		if (isMissingKeys) {
			dispatch(resourceLookup(missingKeys));
		}
	}, [dispatch, missingKeys, isMissingKeys]);

	// Create the resulting object
	const resourcesByKeys = useMemo(
		() =>
			keys.reduce((result, key) => {
				result[key] = allResources[key]; // eslint-disable-line
				return result;
			}, {}),
		[allResources, keys]
	);

	return {
		resources: resourcesByKeys,
		isLoading: isMissingKeys
	};
};

export default useResources;
